import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, MoneySchema, withMeta } from '../common';
import { ScorePillarSchema } from '../dataset/core';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** portal.* — kernel router for the investor portal on its own origin/session domain (SDD §7.4): role investor-viewer,
 *  throttled login, verified-only rendering, watermarked score card, every view audited. */

export const PortalSessionSchema = withMeta({
  sessionToken: z.string(),
  grantId: IdSchema,
  investorName: z.string(),
  traderName: z.string(),
  expiresAt: IsoDateTimeSchema,
  /** Views the grant permits. */
  views: z.array(z.enum(['score', 'terms', 'payments'])),
});
export const PortalScoreViewSchema = withMeta({
  watermark: z.string(),
  score: z.number(),
  tier: z.string(),
  pillars: z.array(ScorePillarSchema),
  verifiedAsOf: IsoDateTimeSchema,
  /** Every view is audited; the audit id is returned so the portal can show it. */
  auditId: IdSchema,
});
export const PortalPaymentsViewSchema = withMeta({
  items: z.array(
    z
      .object({
        id: IdSchema,
        amount: MoneySchema,
        currency: z.string(),
        status: z.enum(['pending', 'paid', 'waived', 'disputed']),
        at: IsoDateTimeSchema,
      })
      .strict(),
  ),
  auditId: IdSchema,
});

export const portalProcedures = defineProcedures({
  'portal.session.login': mutation(
    'Token login (throttled; grant must be active)',
    z.object({ grantToken: z.string().min(8).max(128) }).strict(),
    PortalSessionSchema,
  ),
  'portal.session.logout': mutation('End the portal session', z.void(), withMeta({ ok: z.literal(true) })),
  'portal.views.list': query(
    'Views available to this grant',
    z.void(),
    withMeta({ views: z.array(z.enum(['score', 'terms', 'payments'])) }),
  ),
  'portal.views.score': query('Watermarked, verified-only score card', z.void(), PortalScoreViewSchema),
  'portal.views.payments': query(
    'Payment ledger as the investor sees it',
    z.void(),
    PortalPaymentsViewSchema,
  ),
});
export type PortalFamilyClient = RouterShape<typeof portalProcedures>;

export const portalFamily = defineFamily({
  name: 'portal',
  title: 'Investor portal',
  screens: ['Investor portal', 'Investors'],
  procedures: portalProcedures,
  binding: 'simulated',
});
